import { useState, useEffect, useCallback } from 'react'
import { Hub } from 'aws-amplify/utils'
import {
  signInWithRedirect,
  signIn as amplifySignIn,
  confirmSignIn as amplifyConfirmSignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth'

export interface User {
  userId: string
  username: string
  email?: string
  role?: string // "admin" or undefined
  allowedSites?: string[] // e.g. ["sawafuji"]
  allowedApps?: string[] // e.g. ["portal", "api", "monitoring"] or ["*"]
}

export type OtpStep = 'IDLE' | 'CODE_SENT' | 'DONE'

export class EmailOtpUnavailableError extends Error {
  constructor() {
    super('Email OTP is not available for this account.')
    this.name = 'EmailOtpUnavailableError'
  }
}

interface UseAuthReturn {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  signInWithGoogle: () => Promise<void>
  requestEmailOtp: (email: string) => Promise<OtpStep>
  confirmEmailOtp: (code: string) => Promise<void>
  signOut: () => Promise<void>
}

function parseJsonClaim(value: unknown): string[] {
  if (!value || typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return []
  }
}

// 「OTP しか使わない」前提でも Cognito の signUp はパスワード必須なので、
// 推測不能なダミーパスワードをその場で生成して捨てる。ユーザーは触らない。
function generateThrowawayPassword(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  const base = btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '')
  // Cognito のデフォルトパスワードポリシー (大文字/小文字/数字 8文字以上) を満たす形で組み立てる
  return `Aa1!${base}`
}

async function initiateEmailOtpSignIn(email: string): Promise<OtpStep> {
  const result = await amplifySignIn({
    username: email,
    options: {
      authFlowType: 'USER_AUTH',
      preferredChallenge: 'EMAIL_OTP',
    },
  })

  if (result.isSignedIn) return 'DONE'

  // Cognito が EMAIL_OTP を発行できた場合のみ次のステップは code 入力。
  // それ以外 (e.g. 既存の Google 連携ユーザーで EMAIL_OTP が使えない場合) は
  // SELECT_CHALLENGE 等が返るので、OTP 不可エラーとして扱う。
  if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
    return 'CODE_SENT'
  }

  throw new EmailOtpUnavailableError()
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    setIsLoading(true)
    try {
      const authUser = await getCurrentUser()
      const session = await fetchAuthSession()
      const payload = session.tokens?.idToken?.payload
      setUser({
        userId: authUser.userId,
        username: authUser.username,
        email: payload?.email as string | undefined,
        role: (payload?.['custom:role'] as string) || undefined,
        allowedSites: parseJsonClaim(payload?.['custom:allowed_sites']),
        allowedApps: parseJsonClaim(payload?.['custom:allowed_apps']),
      })
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAuth()

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuth()
          break
        case 'signedOut':
          setUser(null)
          break
        case 'tokenRefresh':
          checkAuth()
          break
      }
    })

    return () => {
      unsubscribe()
    }
  }, [checkAuth])

  const signInWithGoogle = useCallback(async () => {
    await signInWithRedirect({ provider: 'Google' })
  }, [])

  const requestEmailOtp = useCallback(async (email: string): Promise<OtpStep> => {
    const trimmed = email.trim().toLowerCase()
    // ユーザープールは prevent_user_existence_errors=ENABLED のため、
    // signIn は存在しないユーザーでもダミーの challenge を返す (SES 送信は実際には起きない)。
    // よって signIn 単独では新規 / 既存を区別できない。
    // 先に signUp を試して「既存ユーザー」エラーだけ無視し、access_rules で弾かれる場合 (UserLambdaValidationException)
    // は呼び出し元へ伝播させてエラー表示する。
    try {
      await amplifySignUp({
        username: trimmed,
        password: generateThrowawayPassword(),
        options: { userAttributes: { email: trimmed } },
      })
    } catch (err) {
      const error = err as { name?: string }
      if (error.name !== 'UsernameExistsException') throw err
    }
    return await initiateEmailOtpSignIn(trimmed)
  }, [])

  const confirmEmailOtp = useCallback(async (code: string) => {
    const result = await amplifyConfirmSignIn({ challengeResponse: code.trim() })
    if (!result.isSignedIn) {
      throw new Error('Sign-in did not complete')
    }
  }, [])

  const signOut = useCallback(async () => {
    await amplifySignOut()
    setUser(null)
  }, [])

  return {
    user,
    isAuthenticated: user !== null,
    isLoading,
    signInWithGoogle,
    requestEmailOtp,
    confirmEmailOtp,
    signOut,
  }
}
